import { Link } from "@tanstack/react-router";
import { Bell, Inbox } from "lucide-react";
import { type ReactNode, useState } from "react";
import { ROUTES } from "@/app/routes";
import { NotificationRow } from "@/features/notifications/components/notification-row";
import { useMarkAllNotificationsRead } from "@/features/notifications/hooks/use-notification-mutations";
import { useNotifications } from "@/features/notifications/hooks/use-notifications";
import { useUnreadNotificationCount } from "@/features/notifications/hooks/use-unread-count";
import type { NotificationRow as NotificationRowDto } from "@/features/notifications/schemas/notification.schema";
import { Permission } from "@/shared/auth/permissions";
import { useCanDo } from "@/shared/auth/use-can-do";
import { Button } from "@/shared/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";
import { cn } from "@/shared/lib/cn";

interface Props {
  workspaceId: string;
}

/// Slice 2, the live bell. Polls unread-count every 30s; opening the
/// dropdown fetches the last 10 notifications. Click anywhere on a row
/// marks it read + navigates; "View all" links to the full inbox; "Mark
/// all read" clears the badge.
export function NotificationsBell({ workspaceId }: Props): ReactNode {
  const { workspace } = useActiveWorkspace();
  const allowed = useCanDo(Permission.NOTIFICATION_VIEW_UNREAD_COUNT);
  const [open, setOpen] = useState(false);

  const unreadQuery = useUnreadNotificationCount(allowed ? workspaceId : undefined);
  // Lazy-fetch dropdown contents only after the user opens it once.
  // TanStack Query keeps the result cached for subsequent opens.
  const listQuery = useNotifications(allowed && open ? workspaceId : undefined, { limit: 10 });
  const markAll = useMarkAllNotificationsRead(workspaceId);

  if (!allowed) return null;

  const unread = unreadQuery.data?.count ?? 0;
  const rows = listQuery.data?.rows ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* aria-live region announces unread-count changes to AT users even
          when focus is elsewhere, the bell button's aria-label updates
          silently otherwise. */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {unread > 0
          ? `${unread} unread notification${unread === 1 ? "" : "s"}`
          : "No unread notifications"}
      </span>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : "Notifications, no unread items"
          }
          className="relative"
        >
          <Bell aria-hidden="true" className="size-4" />
          {unread > 0 ? (
            <span
              aria-hidden="true"
              className={cn(
                "absolute -top-0.5 -right-0.5 inline-flex h-4 min-w-4 items-center justify-center",
                "rounded-full bg-pulse px-1 text-3xs font-semibold text-white",
              )}
            >
              {unread > 99 ? "99+" : unread}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <header className="flex items-center justify-between gap-2 px-3 py-2.5">
          <span className="text-sm font-semibold text-text-primary">Notifications</span>
          <div className="flex items-center gap-2">
            {unread > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => markAll.mutate(undefined)}
                disabled={markAll.isPending}
                className="h-7 px-2 text-xs"
              >
                Mark all read
              </Button>
            ) : null}
            <Link
              to={ROUTES.workspace.notifications.path}
              params={{ workspaceSlug: workspace.slug }}
              onClick={() => setOpen(false)}
              className="text-xs text-text-tertiary transition-colors hover:text-foreground"
            >
              View all
            </Link>
          </div>
        </header>
        <div className="border-t border-border" aria-hidden="true" />
        <DropdownBody
          loading={listQuery.isLoading}
          error={listQuery.error as Error | null}
          rows={rows}
          workspaceId={workspaceId}
          workspaceSlug={workspace.slug}
          onAfterClick={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

function DropdownBody({
  loading,
  error,
  rows,
  workspaceId,
  workspaceSlug,
  onAfterClick,
}: {
  loading: boolean;
  error: Error | null;
  rows: ReadonlyArray<NotificationRowDto>;
  workspaceId: string;
  workspaceSlug: string;
  onAfterClick: () => void;
}): ReactNode {
  if (error && rows.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-danger-fg">
        Couldn't load notifications. Try again in a moment.
      </div>
    );
  }
  if (loading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-2 p-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-md" />
        ))}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
        <Inbox aria-hidden="true" className="size-5 text-text-quaternary" />
        <p className="text-sm font-medium text-text-primary">No notifications yet</p>
        <p className="text-xs text-text-tertiary">We'll let you know when someone needs you.</p>
      </div>
    );
  }
  return (
    <ul className="max-h-96 divide-y divide-border-subtle overflow-y-auto">
      {rows.map((row) => (
        <li key={row.id}>
          <NotificationRow
            workspaceId={workspaceId}
            workspaceSlug={workspaceSlug}
            notification={row}
            compact
            onAfterClick={onAfterClick}
          />
        </li>
      ))}
    </ul>
  );
}
